class AMVideo extends HTMLElement {
  static MOBILE_QUERY = '(max-width: 767px)';

  connectedCallback() {
    this.muteToggle = this.querySelector('[data-am-video-mute-toggle]');
    this.playToggle = this.querySelector('[data-am-video-play-toggle]');
    this.videos = Array.from(this.querySelectorAll('video[data-am-video-source]'));
    if (!this.videos.length) return;

    this.boundOnMuteClick = this.onMuteClick.bind(this);
    this.boundOnPlayClick = this.onPlayClick.bind(this);
    this.boundOnPlay = this.onPlay.bind(this);
    this.boundOnPause = this.onPause.bind(this);
    this.boundOnVolumeChange = this.onVolumeChange.bind(this);

    this.pickActive();
    this.muteToggle?.addEventListener('click', this.boundOnMuteClick);
    this.playToggle?.addEventListener('click', this.boundOnPlayClick);
  }

  disconnectedCallback() {
    this.muteToggle?.removeEventListener('click', this.boundOnMuteClick);
    this.playToggle?.removeEventListener('click', this.boundOnPlayClick);
    this.videos?.forEach((v) => {
      v.removeEventListener('play', this.boundOnPlay);
      v.removeEventListener('pause', this.boundOnPause);
      v.removeEventListener('volumechange', this.boundOnVolumeChange);
    });
  }

  pickActive() {
    const hasDesktop = this.dataset.hasDesktop === 'true';
    const hasMobile = this.dataset.hasMobile === 'true';
    let activeKind;
    if (hasDesktop && hasMobile) {
      activeKind = window.matchMedia(AMVideo.MOBILE_QUERY).matches ? 'mobile' : 'desktop';
    } else {
      activeKind = hasDesktop ? 'desktop' : 'mobile';
    }

    this.videos.forEach((video) => {
      const kind = video.dataset.amVideoSource;
      if (kind === activeKind) {
        this.activeVideo = video;
        video.muted = true;
        video.addEventListener('play', this.boundOnPlay);
        video.addEventListener('pause', this.boundOnPause);
        video.addEventListener('volumechange', this.boundOnVolumeChange);
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      } else {
        video.pause();
        video.removeAttribute('autoplay');
        video.preload = 'none';
      }
    });

    this.syncMuteState();
    this.syncPlayState();
  }

  onMuteClick() {
    if (!this.activeVideo) return;
    this.activeVideo.muted = !this.activeVideo.muted;
  }

  onPlayClick() {
    if (!this.activeVideo) return;
    if (this.activeVideo.paused) {
      const promise = this.activeVideo.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    } else {
      this.activeVideo.pause();
    }
  }

  onPlay() {
    this.syncPlayState();
  }

  onPause() {
    this.syncPlayState();
  }

  onVolumeChange() {
    this.syncMuteState();
  }

  syncMuteState() {
    if (!this.muteToggle || !this.activeVideo) return;
    const muted = this.activeVideo.muted;
    this.muteToggle.setAttribute('aria-pressed', muted ? 'true' : 'false');
    const labelKey = muted ? 'unmute' : 'mute';
    const label = this.muteToggle.dataset[`label${labelKey.charAt(0).toUpperCase()}${labelKey.slice(1)}`];
    if (label) this.muteToggle.setAttribute('aria-label', label);
  }

  syncPlayState() {
    if (!this.playToggle || !this.activeVideo) return;
    const paused = this.activeVideo.paused;
    this.playToggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    const labelKey = paused ? 'play' : 'pause';
    const label = this.playToggle.dataset[`label${labelKey.charAt(0).toUpperCase()}${labelKey.slice(1)}`];
    if (label) this.playToggle.setAttribute('aria-label', label);
  }
}

if (!customElements.get('am-video')) {
  customElements.define('am-video', AMVideo);
}
